import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle,
  ChevronRight,
  ChevronDown,
  Circle,
  Clock,
  Loader2,
  Sparkles,
  XCircle,
  Zap,
  RefreshCw,
  AlertTriangle,
  BookOpen,
  Eye,
  Image,
  Tag,
  Users,
  TrendingUp,
  Route,
  Lightbulb,
  PlusCircle,
  ArrowRight,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { learningPathApiService } from '@/services/api/learning-path-api.service';
import { CardSkeleton, SkeletonLoader } from '@/components/ui/SkeletonLoader';
import useHasPermission from '@/hooks/useHasPermission';
import { LEARNING_PATH_CATALOG_PERMISSION } from '@/constants/constant-permissions';

type StageKey = 'stage_1_metadata' | 'stage_2_images' | 'stage_3_courses';

interface GenerationStage {
  key: StageKey;
  label: string;
  description: string;
  icon: React.ComponentType<any>;
  color: string;
}

export default function LearningPathGenerationStatus() {
  const { orchestration_id } = useParams<{ orchestration_id: string }>();
  const navigate = useNavigate();

  const [isGenerating, setIsGenerating] = useState(true);
  const [generationStatus, setGenerationStatus] = useState<any>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [expandedStages, setExpandedStages] = useState<Set<string>>(new Set());
  const [isRetrying, setIsRetrying] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [stageElapsedTimes, setStageElapsedTimes] = useState<Record<string, number>>({});

  const canViewDetails = useHasPermission([LEARNING_PATH_CATALOG_PERMISSION.ADMIN_LEARNING_PATH_CATALOG_WRITE]);
  const canGoToLearningPaths = useHasPermission([
    LEARNING_PATH_CATALOG_PERMISSION.ADMIN_LEARNING_PATH_CATALOG_READ,
    LEARNING_PATH_CATALOG_PERMISSION.ADMIN_LEARNING_PATH_CATALOG_WRITE,
  ]);

  // Track user-manually opened stages to prevent auto-collapse
  const userOpenedStagesRef = useRef<Set<string>>(new Set());

  // Generation stages configuration - Memoized to prevent infinite loops
  const stages: GenerationStage[] = useMemo(
    () => [
      {
        key: 'stage_1_metadata',
        label: 'Stage 1: Metadata Generation',
        description: 'AI generating path title, description, outcomes, and tags',
        icon: Sparkles,
        color: 'from-purple-500 to-indigo-600',
      },
      {
        key: 'stage_2_images',
        label: 'Stage 2: Image Generation',
        description: 'AI generating professional banner and thumbnail images',
        icon: Image,
        color: 'from-pink-500 to-rose-600',
      },
      {
        key: 'stage_3_courses',
        label: 'Stage 3: Course Selection',
        description: 'AI selecting and sequencing courses from published catalog',
        icon: BookOpen,
        color: 'from-green-500 to-emerald-600',
      },
    ],
    []
  );

  // Calculate overall elapsed time in real-time
  useEffect(() => {
    if (!generationStatus?.started_at) return;

    const updateElapsedTime = () => {
      const startTime = new Date(generationStatus.started_at).getTime();
      const now = Date.now();
      const elapsed = now - startTime;
      setElapsedTime(elapsed);
    };

    updateElapsedTime();

    if (isGenerating) {
      const interval = setInterval(updateElapsedTime, 1000);
      return () => clearInterval(interval);
    }
  }, [generationStatus?.started_at, isGenerating]);

  // Calculate elapsed time for each stage in real-time
  useEffect(() => {
    if (!generationStatus) return;

    const updateStageElapsedTimes = () => {
      const newTimes: Record<string, number> = {};

      stages.forEach((stage) => {
        const stageData = generationStatus[stage.key];
        if (!stageData) return;

        const status = stageData.status?.toLowerCase();

        if (status === 'processing' || status === 'in_progress') {
          const startedAt = stageData.start_time;
          if (startedAt) {
            const startTime = new Date(startedAt).getTime();
            const now = Date.now();
            newTimes[stage.key] = now - startTime;
          }
        } else if ((status === 'completed' || status === 'failed') && stageData.duration_ms) {
          newTimes[stage.key] = stageData.duration_ms;
        }
      });

      setStageElapsedTimes(newTimes);
    };

    updateStageElapsedTimes();

    if (isGenerating) {
      const interval = setInterval(updateStageElapsedTimes, 1000);
      return () => clearInterval(interval);
    }
  }, [generationStatus, isGenerating, stages]);

  // Toggle stage expansion
  const toggleStageExpansion = (stageKey: string) => {
    setExpandedStages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(stageKey)) {
        newSet.delete(stageKey);
        userOpenedStagesRef.current.delete(stageKey);
      } else {
        newSet.add(stageKey);
        userOpenedStagesRef.current.add(stageKey);
      }
      return newSet;
    });
  };

  // Load generation status
  const loadGenerationStatus = useCallback(async () => {
    if (!orchestration_id) return;

    try {
      setIsLoadingStatus(true);
      const status = await learningPathApiService.getLearningPathStatus(orchestration_id);
      setGenerationStatus(status);

      const overallStatus = status.overall_status?.toLowerCase();
      const isStillGenerating =
        overallStatus === 'pending' || overallStatus === 'in_progress' || overallStatus === 'processing';
      setIsGenerating(isStillGenerating);

      // Auto-expand active or failed stages
      setExpandedStages((prev) => {
        const newSet = new Set(prev);
        stages.forEach((stage) => {
          const stageStatus = status[stage.key]?.status?.toLowerCase();
          const isUserOpened = userOpenedStagesRef.current.has(stage.key);

          if (stageStatus === 'processing' || stageStatus === 'in_progress' || stageStatus === 'failed') {
            newSet.add(stage.key);
          } else if (stageStatus === 'completed' && !isUserOpened) {
            newSet.delete(stage.key);
          }
        });
        return newSet;
      });
    } catch (error: any) {
      console.error('Error loading generation status:', error);
      if (error.response?.status !== 404) {
        toast.error('Failed to load generation status');
      }
    } finally {
      setIsLoadingStatus(false);
    }
  }, [orchestration_id, stages]);

  // Main useEffect
  useEffect(() => {
    if (!orchestration_id) {
      toast.error('No orchestration ID found');
      navigate('/admin/learning-paths');
      return;
    }

    loadGenerationStatus();
  }, [orchestration_id, navigate, loadGenerationStatus]);

  // Polling every 5 seconds
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isGenerating) {
      timer = setInterval(() => {
        loadGenerationStatus();
      }, 5000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isGenerating, loadGenerationStatus]);

  const getStageStatus = (stageName: string) => {
    if (!generationStatus) return { status: 'pending', icon: Circle, color: 'text-gray-400' };

    const stageData = generationStatus[stageName];
    const status = stageData?.status?.toLowerCase() || 'pending';

    switch (status) {
      case 'completed':
        return { status, icon: CheckCircle, color: 'text-brand-green-600' };
      case 'processing':
      case 'in_progress':
        return { status: 'processing', icon: Loader2, color: 'text-brand-blue-600', animate: true };
      case 'failed':
        return { status, icon: XCircle, color: 'text-red-600' };
      default:
        return { status: 'pending', icon: Circle, color: 'text-gray-400' };
    }
  };

  const handleRetry = async () => {
    if (!orchestration_id) return;

    setIsRetrying(true);
    try {
      await learningPathApiService.retryGeneration(orchestration_id);
      toast.success('Learning path generation restarted successfully!');
      setIsGenerating(true);
      setTimeout(() => {
        loadGenerationStatus();
      }, 2000);
    } catch (error: any) {
      console.error('Retry error:', error);
      toast.error(error.response?.data?.message || 'Failed to retry generation');
    } finally {
      setIsRetrying(false);
    }
  };

  if (!orchestration_id) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Missing Orchestration ID</h2>
          <p className="text-gray-600 mb-4">No orchestration ID provided.</p>
          <button
            onClick={() => navigate('/admin/learning-paths')}
            className="px-4 py-2 bg-brand-purple-600 text-white rounded-lg hover:bg-brand-purple-700"
          >
            Go to Learning Paths
          </button>
        </div>
      </div>
    );
  }

  const renderTimelineItem = (stage: GenerationStage, index: number) => {
    const stageStatus = getStageStatus(stage.key);
    const IconComponent = stageStatus.icon;
    const isCompleted = stageStatus.status === 'completed';
    const stageData = generationStatus?.[stage.key];
    const actualStageStatus = stageData?.status?.toLowerCase();
    const isActive = actualStageStatus === 'processing' || actualStageStatus === 'in_progress';
    const isFailed = stageStatus.status === 'failed';
    const isLast = index === stages.length - 1;
    const isExpanded = expandedStages.has(stage.key);
    const hasError = stageData?.error_details;

    return (
      <div key={stage.key} className="relative">
        {/* Timeline Line */}
        {!isLast && (
          <div
            className={`absolute left-6 top-16 w-0.5 h-20 ${
              isCompleted ? 'bg-gradient-to-b from-brand-green-500 to-brand-emerald-600' : 'bg-gray-300'
            }`}
          ></div>
        )}

        {/* Timeline Item */}
        <div className="flex gap-4">
          {/* Status Circle */}
          <div
            className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center border-2 relative ${
              isCompleted
                ? 'bg-gradient-to-br from-brand-green-100 to-brand-emerald-100 border-brand-green-500 shadow-lg'
                : isActive
                ? 'bg-gradient-to-br from-brand-blue-100 to-brand-cyan-100 border-brand-blue-500 shadow-lg animate-pulse'
                : isFailed
                ? 'bg-gradient-to-br from-red-100 to-rose-100 border-red-500 shadow-lg'
                : 'bg-gradient-to-br from-gray-100 to-slate-100 border-gray-300'
            }`}
          >
            <IconComponent
              className={`w-6 h-6 ${
                isCompleted ? 'text-brand-green-600' : isActive ? 'text-brand-blue-600' : isFailed ? 'text-red-600' : 'text-gray-400'
              } ${stageStatus.animate ? 'animate-spin' : ''}`}
            />
          </div>

          {/* Content */}
          <div className="flex-1 pb-8">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300">
              {/* Header */}
              <div
                className={`p-6 ${isExpanded ? 'border-b border-gray-100 rounded-t-2xl' : 'rounded-2xl'} ${
                  isActive || isFailed || isCompleted ? 'cursor-pointer hover:bg-opacity-80' : ''
                } ${
                  isCompleted
                    ? 'bg-gradient-to-r from-brand-green-50 to-brand-emerald-50'
                    : isActive
                    ? 'bg-gradient-to-r from-brand-blue-50 to-brand-cyan-50'
                    : isFailed
                    ? 'bg-gradient-to-r from-red-50 to-rose-50'
                    : 'bg-gradient-to-r from-gray-50 to-slate-50'
                }`}
                onClick={isActive || isFailed || isCompleted ? () => toggleStageExpansion(stage.key) : undefined}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{stage.label}</h3>
                    <p className="text-sm text-gray-600">{stage.description}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Stage elapsed time */}
                    {stageElapsedTimes[stage.key] && (
                      <div
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md ${
                          isActive
                            ? 'bg-brand-blue-100 text-brand-blue-700 font-semibold'
                            : isCompleted
                            ? 'bg-brand-green-100 text-brand-green-700 font-medium'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        <Clock className={`w-3.5 h-3.5 ${isActive ? 'animate-spin' : ''}`} />
                        <span className="text-sm">{Math.round(stageElapsedTimes[stage.key] / 1000)}s</span>
                        {isActive && <span className="animate-pulse text-brand-blue-500">●</span>}
                      </div>
                    )}
                    <div
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        isCompleted
                          ? 'bg-green-100 text-green-800'
                          : isActive
                          ? 'bg-blue-100 text-blue-800'
                          : isFailed
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {isCompleted ? 'Completed' : isActive ? 'In Progress' : isFailed ? 'Failed' : 'Pending'}
                    </div>
                    {(isActive || isFailed || isCompleted) && (
                      <div className="text-gray-400 hover:text-gray-600">
                        {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Expandable Content */}
              {isExpanded && (
                <>
                  {/* Progress Bar */}
                  {isActive && (
                    <div className="px-6 py-4 border-t border-gray-100 bg-gradient-to-r from-brand-blue-50 to-brand-cyan-50">
                      <div className="flex items-center justify-between text-sm text-brand-blue-600 mb-2">
                        <span className="font-medium">Processing...</span>
                      </div>
                      <div className="w-full bg-brand-blue-200 rounded-full h-3 overflow-hidden">
                        <div
                          className="h-3 bg-gradient-to-r from-brand-blue-500 to-brand-cyan-600 rounded-full animate-pulse"
                          style={{ width: '65%' }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Completion Status */}
                  {isCompleted && (
                    <div className="p-6 border-t border-gray-100 bg-gradient-to-r from-green-50 to-emerald-50 rounded-b-2xl">
                      <div className="flex items-center gap-2 text-green-800 mb-3">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Stage completed successfully</span>
                      </div>

                      {/* Stage 1: Metadata Sample */}
                      {stage.key === 'stage_1_metadata' && generationStatus?.path_title && (
                        <div className="mt-3 p-4 bg-white rounded-lg border border-green-200">
                          <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="w-4 h-4 text-green-600" />
                            <span className="font-medium text-green-800 text-sm">Generated Metadata</span>
                          </div>
                          <div className="space-y-3 text-sm">
                            <div>
                              <span className="font-semibold text-green-800">Title:</span>
                              <p className="text-green-700 mt-1">{generationStatus.path_title}</p>
                            </div>
                            {generationStatus.description && (
                              <div>
                                <span className="font-semibold text-green-800">Description:</span>
                                <p className="text-green-700 mt-1 line-clamp-3 break-words" title={generationStatus.description}>{generationStatus.description}</p>
                              </div>
                            )}
                            {generationStatus.target_role_or_skill && (
                              <div>
                                <span className="font-semibold text-green-800">Target Role:</span>
                                <p className="text-green-700 mt-1">{generationStatus.target_role_or_skill}</p>
                              </div>
                            )}
                            {generationStatus.tags && generationStatus.tags.length > 0 && (
                              <div>
                                <span className="font-semibold text-green-800">Tags:</span>
                                <div className="flex flex-wrap gap-1.5 mt-1">
                                  {generationStatus.tags.slice(0, 5).map((tag: string, idx: number) => (
                                    <span
                                      key={idx}
                                      className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full border border-green-300"
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                  {generationStatus.tags.length > 5 && (
                                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full border border-green-300">
                                      +{generationStatus.tags.length - 5} more
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Stage 2: Images Sample */}
                      {stage.key === 'stage_2_images' &&
                        (generationStatus?.banner_image_url || generationStatus?.thumbnail_image_url) && (
                          <div className="mt-3 p-4 bg-white rounded-lg border border-green-200">
                            <div className="flex items-center gap-2 mb-3">
                              <Image className="w-4 h-4 text-green-600" />
                              <span className="font-medium text-green-800 text-sm">Generated Images</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {generationStatus.banner_image_url && (
                                <div className="space-y-2">
                                  <p className="text-xs font-medium text-gray-600">Banner Image</p>
                                  <img
                                    src={generationStatus.banner_image_url}
                                    alt="Banner"
                                    className="rounded-lg border-2 border-gray-200 shadow-sm w-full h-auto hover:shadow-md transition-shadow"
                                  />
                                </div>
                              )}
                              {generationStatus.thumbnail_image_url && (
                                <div className="space-y-2">
                                  <p className="text-xs font-medium text-gray-600">Thumbnail Image</p>
                                  <img
                                    src={generationStatus.thumbnail_image_url}
                                    alt="Thumbnail"
                                    className="rounded-lg border-2 border-gray-200 shadow-sm w-full h-auto hover:shadow-md transition-shadow"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                      {/* Stage 3: Courses Count */}
                      {stage.key === 'stage_3_courses' &&
                        generationStatus?.stage_3_courses?.metadata?.courses_found !== undefined && (
                          <div className="mt-3 p-3 bg-white rounded-lg border border-green-200 text-sm text-green-700">
                            <strong>Courses Found:</strong> {generationStatus.stage_3_courses.metadata.courses_found}
                          </div>
                        )}
                    </div>
                  )}

                  {/* Error Status */}
                  {isFailed && (
                    <div className="p-6 border-t border-gray-100 bg-gradient-to-r from-red-50 to-rose-50 rounded-b-2xl">
                      <div className="flex items-center gap-2 text-red-800 mb-2">
                        <XCircle className="w-5 h-5" />
                        <span className="font-medium">Stage failed</span>
                      </div>
                      {hasError && (
                        <p className="text-sm text-red-700 mt-2 p-3 bg-white rounded-lg border border-red-200">
                          {stageData.error_details}
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Show skeleton loader for initial load
  if (isLoadingStatus && !generationStatus) {
    return (
      <div className="min-h-[calc(100vh-5.25rem)] bg-gradient-to-br from-gray-50 via-brand-purple-50/30 to-brand-pink-50/30">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <button
            onClick={() => navigate('/admin/learning-paths/create')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-6 group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span>Back to Create Learning Path</span>
          </button>

          {/* Hero Header Skeleton */}
          <div className="bg-gradient-to-br from-brand-purple-600 via-brand-pink-600 to-brand-pink-600 rounded-3xl shadow-2xl p-8 mb-6 relative overflow-hidden">
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <SkeletonLoader type="custom" width="56px" height="56px" className="rounded-xl bg-white/20" />
                <div className="flex-1">
                  <SkeletonLoader type="custom" width="300px" height="32px" className="mb-2 bg-white/20" />
                  <SkeletonLoader type="custom" width="250px" height="16px" className="bg-white/10" />
                </div>
              </div>
              <SkeletonLoader type="custom" width="100%" height="20px" className="bg-white/10" />
            </div>
          </div>

          {/* Metrics Skeletons */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-xl shadow-md p-5 border border-gray-200">
                <div className="flex items-center gap-3">
                  <SkeletonLoader type="custom" width="48px" height="48px" className="rounded-lg" />
                  <div className="flex-1">
                    <SkeletonLoader type="text" className="h-3 w-20 mb-2" />
                    <SkeletonLoader type="text" className="h-6 w-12" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Stages Skeletons */}
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <CardSkeleton key={i} showImage={false} lines={2} className="border border-gray-200" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-5.25rem)] bg-gradient-to-br from-gray-50 via-brand-purple-50/30 to-brand-pink-50/30">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin/learning-paths/create')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-6 group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span>Back to Create Learning Path</span>
          </button>

          {/* Hero Header */}
          <div className="bg-gradient-to-br from-brand-purple-600 via-brand-pink-600 to-brand-pink-600 rounded-3xl shadow-2xl p-8 mb-6 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-brand-purple-600/20 via-transparent to-brand-pink-600/20"></div>
            <div className="relative z-10">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                      <Route className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h1 className="text-3xl font-bold">AI Learning Path Generation</h1>
                      <p className="text-brand-purple-100 text-sm mt-1">
                        {generationStatus?.path_title || 'Generating your learning path...'}
                      </p>
                    </div>
                  </div>
                </div>
                <div
                  className={`px-4 py-2 rounded-full backdrop-blur-sm border-2 font-semibold text-sm ${
                    generationStatus?.overall_status?.toLowerCase() === 'completed'
                      ? 'bg-green-500/20 border-green-300 text-white'
                      : isGenerating
                      ? 'bg-yellow-500/20 border-yellow-300 text-white animate-pulse'
                      : 'bg-red-500/20 border-red-300 text-white'
                  }`}
                >
                  {generationStatus?.overall_status?.toLowerCase() === 'completed'
                    ? '✓ Completed'
                    : isGenerating
                    ? '⚡ Generating'
                    : '✗ Failed'}
                </div>
              </div>

              {/* Status Message */}
              <p className="text-brand-purple-100 mb-4">
                {isGenerating ? (
                  <span className="flex items-center gap-2">
                    <Zap className="w-4 h-4 animate-pulse" />
                    AI is crafting your learning path through 3 intelligent stages...
                  </span>
                ) : generationStatus?.overall_status?.toLowerCase() === 'completed' ? (
                  <span className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Your learning path has been successfully generated and is ready to publish!
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    Generation encountered an issue. You can retry below.
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Metrics Dashboard */}
          {generationStatus && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-xl shadow-md p-5 border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-gradient-to-br from-brand-purple-100 to-brand-pink-100 rounded-lg">
                    <BookOpen className="w-5 h-5 text-brand-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 font-medium mb-0.5">Courses</p>
                    <p className="text-xl font-bold text-gray-900">
                      {generationStatus.no_courses_found ? 0 : generationStatus.total_courses || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md p-5 border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-gradient-to-br from-brand-green-100 to-brand-emerald-100 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-brand-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 font-medium mb-0.5">Completed Stages</p>
                    <p className="text-xl font-bold text-gray-900">
                      {[
                        generationStatus.stage_1_metadata?.status,
                        generationStatus.stage_2_images?.status,
                        generationStatus.stage_3_courses?.status,
                      ].filter((s) => s?.toLowerCase() === 'completed').length}
                      /3
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md p-5 border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-gradient-to-br from-brand-blue-100 to-brand-cyan-100 rounded-lg">
                    <Clock className="w-5 h-5 text-brand-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 font-medium mb-0.5">
                      {isGenerating ? 'Elapsed Time' : 'Total Duration'}
                    </p>
                    <p className="text-xl font-bold text-gray-900">
                      {isGenerating
                        ? `${Math.floor(elapsedTime / 1000)}s`
                        : generationStatus.total_duration_ms
                        ? `${Math.round(generationStatus.total_duration_ms / 1000)}s`
                        : '0s'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md p-5 border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-gradient-to-br from-brand-pink-100 to-brand-pink-100 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-brand-pink-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 font-medium mb-0.5">Progress</p>
                    <p className="text-xl font-bold text-gray-900">{generationStatus.progress_percentage || 0}%</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* No Courses Warning with Course Generation Suggestions */}
          {generationStatus?.no_courses_found && (
            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-2xl shadow-lg p-6 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-yellow-600 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-yellow-900 mb-2">No Matching Courses Found</h3>
                  <p className="text-sm text-yellow-800 mb-4">
                    {generationStatus.no_courses_reason || 'AI could not find courses matching the learning goal.'}
                  </p>

                  {/* Course Generation Suggestions */}
                  {generationStatus.course_generation_suggestions && generationStatus.course_generation_suggestions.length > 0 && (
                    <div className="mt-4 space-y-3">
                      <h4 className="font-semibold text-yellow-900 flex items-center gap-2">
                        <PlusCircle className="w-5 h-5" />
                        Suggested Courses to Generate
                      </h4>
                      <p className="text-sm text-yellow-700 mb-3">
                        The following courses would help complete this learning path:
                      </p>
                      <div className="space-y-3">
                        {generationStatus.course_generation_suggestions.map((suggestion: any, index: number) => (
                          <div
                            key={index}
                            className="bg-white rounded-xl border border-yellow-200 p-4 hover:shadow-md transition-all"
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h5 className="font-semibold text-gray-900">{suggestion.user_course_idea}</h5>
                                  <span
                                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                      suggestion.importance === 'critical'
                                        ? 'bg-red-100 text-red-700'
                                        : suggestion.importance === 'high'
                                        ? 'bg-orange-100 text-orange-700'
                                        : suggestion.importance === 'medium'
                                        ? 'bg-yellow-100 text-yellow-700'
                                        : 'bg-gray-100 text-gray-700'
                                    }`}
                                  >
                                    {suggestion.importance}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600 mb-2">{suggestion.reason}</p>
                                <div className="flex flex-wrap gap-2 text-xs">
                                  <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
                                    <Users className="w-3 h-3 inline mr-1" />
                                    {suggestion.targeted_audience}
                                  </span>
                                  {suggestion.difficulty_level && (
                                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                                      {suggestion.difficulty_level}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <button
                                onClick={() => {
                                  // Resolve build language:
                                  //   1. suggestion.language — locale stored alongside the
                                  //      suggestion when Gemini generated it (new schema field;
                                  //      will be present for any LP created after the SDK rebuild)
                                  //   2. content_language — fallback when suggestion.language
                                  //      isn't set (legacy LPs) and the LP isn't 'all'
                                  //   3. omit — admin picks at course-build time
                                  const suggestion_lang =
                                    typeof suggestion.language === 'string' &&
                                    suggestion.language.trim() &&
                                    suggestion.language !== 'all'
                                      ? suggestion.language
                                      : null;
                                  const cl = generationStatus.content_language;
                                  const build_language =
                                    suggestion_lang || (cl && cl !== 'all' ? cl : null);
                                  const params: Record<string, string> = {
                                    idea: suggestion.user_course_idea,
                                    audience: suggestion.targeted_audience,
                                    difficulty: suggestion.difficulty_level || 'intermediate',
                                  };
                                  if (build_language) {
                                    params.language = build_language;
                                  }
                                  navigate(`/admin/course/add-course?${new URLSearchParams(params).toString()}`);
                                }}
                                className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-brand-purple-600 to-brand-blue-600 text-white rounded-lg hover:from-brand-purple-700 hover:to-brand-blue-700 transition-colors text-sm font-medium"
                              >
                                <Zap className="w-4 h-4" />
                                <span>Build Course</span>
                                <ArrowRight className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Metadata Preview Card */}
          {generationStatus?.path_title && (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Route className="w-5 h-5 text-brand-purple-600" />
                <h2 className="text-lg font-bold text-gray-900">Learning Path Details</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb className="w-4 h-4 text-brand-purple-500" />
                    <span className="text-sm font-semibold text-gray-700">Title</span>
                  </div>
                  <p className="text-gray-900 font-medium pl-6">{generationStatus.path_title}</p>
                </div>

                {generationStatus.description && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-brand-blue-500" />
                      <span className="text-sm font-semibold text-gray-700">Description</span>
                    </div>
                    <p className="text-gray-600 pl-6 text-sm leading-relaxed">{generationStatus.description}</p>
                  </div>
                )}

                {generationStatus.target_role_or_skill && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-4 h-4 text-brand-green-500" />
                      <span className="text-sm font-semibold text-gray-700">Target Role/Skill</span>
                    </div>
                    <p className="text-gray-600 pl-6 text-sm">{generationStatus.target_role_or_skill}</p>
                  </div>
                )}

                {generationStatus.tags && generationStatus.tags.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Tag className="w-4 h-4 text-brand-pink-500" />
                      <span className="text-sm font-semibold text-gray-700">Tags</span>
                    </div>
                    <div className="flex flex-wrap gap-2 pl-6">
                      {generationStatus.tags.map((tag: string, index: number) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-gradient-to-r from-brand-purple-100 to-brand-pink-100 text-brand-purple-700 text-xs font-medium rounded-full border border-brand-purple-200"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            </div>
          )}
        </div>

        {/* Generation Stages */}
        <div className="space-y-4">{stages.map((stage, index) => renderTimelineItem(stage, index))}</div>

        {/* Action Buttons */}
        <div className="mt-8 flex items-center justify-center gap-4">
          {generationStatus?.overall_status?.toLowerCase() === 'completed' ? (
            <div className="flex flex-col items-center gap-4">
              {/* Success Message */}
              <div className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl shadow-lg">
                <CheckCircle className="w-8 h-8 text-green-600" />
                <div>
                  <p className="font-bold text-green-900 text-lg">All Stages Completed Successfully!</p>
                  <p className="text-sm text-green-700">
                    Your learning path is ready to be published and assigned to learners.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                {generationStatus.learning_path_id && (
                  <button
                    onClick={() => navigate(`/admin/learning-paths/${generationStatus.learning_path_id}`)}
                    disabled={!canViewDetails}
                    title={!canViewDetails ? 'You do not have permission' : undefined}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-brand-purple-600 to-brand-pink-600 text-white rounded-xl hover:from-brand-purple-700 hover:to-brand-pink-700 transition-all font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Eye className="w-5 h-5" />
                    View Learning Path Details
                  </button>
                )}

                <button
                  onClick={() => navigate('/admin/learning-paths')}
                  disabled={!canGoToLearningPaths}
                  title={!canGoToLearningPaths ? 'You do not have permission' : undefined}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-gray-700 border-2 border-gray-300 rounded-xl hover:bg-gray-50 transition-all font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Route className="w-5 h-5" />
                  Go to Learning Paths
                </button>
              </div>
            </div>
          ) : !isGenerating && generationStatus ? (
            <button
              onClick={handleRetry}
              disabled={isRetrying}
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-xl hover:from-orange-700 hover:to-red-700 transition-all font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRetrying ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Retrying Generation...
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5" />
                  Retry Generation
                </>
              )}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
